// Head-to-head ETF comparison tool.
//
// The pair named in the page's front matter is rendered server-side, so the
// table is in the indexed HTML and the page works with JS off. This script adds
// three things on top: the growth projection, live re-rendering when you pick a
// different pair, and navigation to a dedicated /compare/ URL when the pair you
// picked has its own page.

const CURRENCY = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
  maximumFractionDigits: 0,
})

const NUMBER = new Intl.NumberFormat("en-CA")

const REGIONS = [
  ["United States", "us"],
  ["Canada", "canada"],
  ["International developed", "international"],
  ["Emerging markets", "emerging"],
]

// Mirrors the server-side formatter in _comparison_tool.erb: 0.20 must read as
// "0.20%" rather than "0.2%", SPY's 0.0945% keeps its precision, and a whole
// number like a 100% equity weight stays whole.
const pct = (v) => {
  if (v === null || v === undefined) return null
  if (v === Math.round(v)) return `${v}%`
  if (Math.round(v * 100) / 100 === v) return `${v.toFixed(2)}%`
  return `${v}%`
}

const money = (v) => {
  if (v === null || v === undefined) return null
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`
  if (v >= 1e6) return `$${Math.round(v / 1e6)}M`
  return `$${v}`
}

const aumOf = (f) => {
  if (f.aum_cad) return `${money(f.aum_cad)} CAD`
  if (f.aum_usd) return `${money(f.aum_usd)} USD`
  return null
}

// An unknown figure and a zero are different claims; render a dash, never a 0.
const cell = (v) =>
  v === null || v === undefined
    ? '<span class="cmp-unknown" title="Not confirmed">&mdash;</span>'
    : v

const flagged = (f, field) => (f.needs_review || []).includes(field)

const ROWS = [
  { label: "MER", key: "mer", fmt: pct, note: "What actually comes out of your return each year" },
  { label: "Management fee", key: "management_fee", fmt: pct, note: "The manager's cut, before fund operating costs" },
  { label: "Stocks held", key: "holdings_lookthrough", fmt: (v) => NUMBER.format(v), note: "Individual companies you end up owning" },
  { label: "Direct holdings", key: "holdings_direct", fmt: (v) => NUMBER.format(v), note: "What the fund itself holds. Low for a fund of funds" },
  { label: "Assets under management", custom: aumOf, note: "Fund size" },
  { label: "Distribution yield", key: "yield", fmt: pct, note: "Trailing 12-month" },
  { label: "1-year total return", key: "return_1y", fmt: pct, note: "Past performance, not a forecast" },
  { label: "Equity allocation", key: "equity_allocation", fmt: pct, note: "The rest is bonds or cash" },
  { label: "Listed on", key: "exchange" },
  { label: "Currency", key: "currency" },
  { label: "Structure", key: "structure" },
  { label: "Launched", custom: (f) => String(f.inception || "").slice(0, 4) },
]

function renderTable(root, funds, a, b) {
  const fa = funds[a]
  const fb = funds[b]
  const pair = [[a, fa], [b, fb]]

  const head = pair
    .map(([t, f]) => `<th scope="col"><span class="cmp-ticker">${t}</span><span class="cmp-fundname">${f.name}</span></th>`)
    .join("")

  const body = ROWS.map((row) => {
    const cells = pair
      .map(([, f]) => {
        const raw = row.custom ? row.custom(f) : f[row.key]
        const val = raw !== null && raw !== undefined && row.fmt ? row.fmt(raw) : raw
        const flag = row.key && flagged(f, row.key)
        return `<td${flag ? ' class="cmp-flagged"' : ""}>${cell(val)}${
          flag ? '<span class="cmp-flag" title="Not confirmed against a primary source">*</span>' : ""
        }</td>`
      })
      .join("")
    const note = row.note ? `<span class="cmp-note">${row.note}</span>` : ""
    return `<tr><th scope="row">${row.label}${note}</th>${cells}</tr>`
  }).join("")

  const table = root.querySelector(".cmp-table")
  if (table) {
    table.innerHTML =
      `<caption class="cmp-sr">${a} compared with ${b}</caption>` +
      `<thead><tr><th scope="col">Measure</th>${head}</tr></thead><tbody>${body}</tbody>`
  }

  const verdict = root.querySelector(".cmp-verdict")
  if (verdict) {
    const tiles = []
    if (fa.mer != null && fb.mer != null && fa.mer !== fb.mer) {
      const cheaper = fa.mer < fb.mer ? a : b
      const gap = Math.round(Math.abs(fa.mer - fb.mer) * 1000) / 1000
      tiles.push(
        `<div class="cmp-tile"><span class="cmp-tile-num">${cheaper}</span><span class="cmp-tile-label">costs less, by ${gap}% a year</span></div>`
      )
    }
    if (fa.holdings_lookthrough && fb.holdings_lookthrough && fa.holdings_lookthrough !== fb.holdings_lookthrough) {
      const broader = fa.holdings_lookthrough > fb.holdings_lookthrough ? a : b
      tiles.push(
        `<div class="cmp-tile"><span class="cmp-tile-num">${broader}</span><span class="cmp-tile-label">holds more companies</span></div>`
      )
    }
    tiles.push(
      `<div class="cmp-tile"><span class="cmp-tile-num">${fa.allocation.canada}% <span class="cmp-tile-vs">vs</span> ${fb.allocation.canada}%</span><span class="cmp-tile-label">Canadian exposure</span></div>`
    )
    verdict.innerHTML = tiles.join("")
  }

  const geo = root.querySelector(".cmp-geo")
  if (geo) {
    geo.innerHTML = pair
      .map(([t, f]) => {
        const bars = REGIONS.map(([label, key]) => {
          const v = f.allocation[key]
          return `<div class="cmp-bar-row"><span class="cmp-bar-label">${label}</span><span class="cmp-bar-track"><span class="cmp-bar-fill" style="width: ${v}%"></span></span><span class="cmp-bar-val">${v}%</span></div>`
        }).join("")
        const flag = flagged(f, "allocation") ? '<span class="cmp-flag">*</span>' : ""
        return `<div class="cmp-geo-fund"><h4>${t}${flag}</h4>${bars}</div>`
      })
      .join("")
  }
}

function renderProjection(root, funds, a, b) {
  const out = root.querySelector("[data-cmp-results]")
  if (!out) return

  const amount = Number(String(root.querySelector("[data-cmp-amount]")?.value ?? "").replace(/[^0-9.]/g, "")) || 0
  const years = Number(root.querySelector("[data-cmp-years]")?.value) || 0
  const gross = Number(root.querySelector("[data-cmp-return]")?.value) || 0

  if (amount <= 0 || years <= 0) {
    out.innerHTML = '<p class="cmp-result-sub">Enter an amount and a number of years.</p>'
    return
  }

  const project = (fund) => {
    // Net of the MER, which is the whole point of showing this: the fee is a
    // haircut on the compounding rate, not a one-off charge.
    const net = (gross - (fund.mer ?? 0)) / 100
    return amount * Math.pow(1 + net, years)
  }

  const results = [[a, funds[a]], [b, funds[b]]].map(([t, f]) => ({
    ticker: t,
    value: project(f),
    mer: f.mer,
  }))

  const cards = results
    .map(
      (r) =>
        `<div class="cmp-result"><span class="cmp-result-ticker">${r.ticker}</span><span class="cmp-result-val">${CURRENCY.format(
          r.value
        )}</span><span class="cmp-result-sub">after ${years} year${years === 1 ? "" : "s"}, net of a ${
          r.mer ?? 0
        }% MER</span></div>`
    )
    .join("")

  const gap = Math.abs(results[0].value - results[1].value)
  const ahead = results[0].value >= results[1].value ? results[0] : results[1]

  // A fee-only projection flatters the cheaper fund when the two are priced in
  // different currencies: conversion spread, and the withholding tax a
  // US-listed fund attracts in the wrong account, are both real costs this
  // arithmetic does not carry. Say so rather than let the number stand alone.
  const crossCurrency = funds[a].currency !== funds[b].currency
  const caveat = crossCurrency
    ? ` These two are priced in different currencies, so the fee gap is only part of the story: currency conversion and withholding tax can outweigh it entirely. The comparison below covers both.`
    : ""

  const gapNote =
    gap < 1
      ? `<div class="cmp-gap">Identical MERs, so the fee makes no difference here. Pick on allocation and currency instead.${caveat}</div>`
      : `<div class="cmp-gap"><strong>${CURRENCY.format(gap)}</strong> more in ${
          ahead.ticker
        } after ${years} years, purely from the ${Math.round(Math.abs(results[0].mer - results[1].mer) * 1000) / 1000}% fee difference. Both funds are assumed to earn the same ${gross}% before fees.${caveat}</div>`

  out.innerHTML = cards + gapNote
}

export function initComparisonTool() {
  document.querySelectorAll("[data-comparison]").forEach((root) => {
    const payload = root.querySelector("[data-cmp-data]")
    if (!payload) return

    let data
    try {
      data = JSON.parse(payload.textContent)
    } catch (err) {
      console.warn("Comparison tool: could not parse fund data", err)
      return
    }

    const { funds, pairs = {} } = data
    let a = root.dataset.fundA
    let b = root.dataset.fundB

    const selectA = root.querySelector('[data-cmp-select="a"]')
    const selectB = root.querySelector('[data-cmp-select="b"]')

    const update = () => {
      if (!funds[a] || !funds[b]) return
      renderTable(root, funds, a, b)
      renderProjection(root, funds, a, b)
    }

    root.querySelector("[data-cmp-go]")?.addEventListener("click", () => {
      const nextA = selectA?.value
      const nextB = selectB?.value
      if (!nextA || !nextB) return

      if (nextA === nextB) {
        window.alert("Pick two different funds to compare.")
        return
      }

      // A pair with its own page gets the URL, so the comparison is linkable
      // and indexed. Anything else re-renders here.
      const slug = pairs[`${nextA}-${nextB}`] || pairs[`${nextB}-${nextA}`]
      if (slug && slug !== window.location.pathname) {
        window.location.href = slug
        return
      }

      a = nextA
      b = nextB
      update()
    })

    ;["[data-cmp-amount]", "[data-cmp-years]", "[data-cmp-return]"].forEach((sel) => {
      root.querySelector(sel)?.addEventListener("input", () => renderProjection(root, funds, a, b))
    })

    // Display the amount with thousands separators. The input is type=text so
    // the formatted value survives; renderProjection strips non-digits when
    // reading it. Reformat on change (blur) rather than on input so the caret
    // is never fought mid-typing.
    const amountInput = root.querySelector("[data-cmp-amount]")
    amountInput?.addEventListener("change", () => {
      const n = Number(amountInput.value.replace(/[^0-9.]/g, ""))
      if (n > 0) amountInput.value = n.toLocaleString("en-CA")
    })

    // Only the projection needs painting on load; the table is already server-rendered.
    renderProjection(root, funds, a, b)
  })
}
